"use client";

import type { FC } from "react";

import { isEditing } from "@builder.io/sdk-react";

import { OnsitePersonalizationModal as OnsitePersonalizationModalUI } from "@jti/checkout/OnsitePersonalizationModal";

import { useDisplayModelComponent } from "@/libs/builder/hooks/useDisplayModelComponent";

import type { onsitePersonalizationInputs } from "./OnsitePersonalizationModal.builder";

type BuilderOnsitePersonalizationModalProps = BuilderComponentProps<typeof onsitePersonalizationInputs>;

export type OnsitePersonalizationModalProps = BuilderOnsitePersonalizationModalProps & {
  /**
   * Computed in DefaultLayout from fetched Builder entry order.
   * Lower number means higher arbitration priority.
   */
  priority?: number;
};

export const OnsitePersonalizationModal: FC<OnsitePersonalizationModalProps> = ({
  mainImage,
  mobileImage,
  imageAltText,
  title,
  description,
  primaryButtonText,
  secondaryButtonText,
  primaryButtonUrl,
  secondaryButtonUrl,
  minCartItemCount,
  paragraph,
  couponContent,
  isImageOnLeft,
  inactivityDelay,
  timeOnPageDelay,
  hideModalExpireTime,
  isInactivityModalEnabled,
  isPurchasedDaysAgoModalEnabled,
  purchasedDaysAgoDelay,
  isQuickSwipeUpModalEnabled,
  triggerMode,
  priority,
  isScrollDepthModalEnabled,
  isExitIntentEnabled,
  isTimeOnPageEnabled,
  popupId,
  frequencyType,
  frequencyValue,
  isUserActionTriggerEnabled,
  userActionTrigger,
  isLocalTimeTriggerEnabled,
  localTimeWindow,
  customStartHour,
  customEndHour,
}) => {
  const { isCouponAvailable, coupon, copyCouponLabel, couponHelperText } = couponContent;
  const shouldDisplayModelComponent = useDisplayModelComponent("onsite-personalization-modal");
  if (!shouldDisplayModelComponent) {
    return null;
  }

  return (
    <OnsitePersonalizationModalUI
      title={title}
      description={description}
      primaryButtonText={primaryButtonText}
      secondaryButtonText={secondaryButtonText}
      primaryButtonUrl={primaryButtonUrl}
      secondaryButtonUrl={secondaryButtonUrl}
      minCartItemCount={minCartItemCount}
      mainImage={mainImage}
      mobileImage={mobileImage}
      imageAltText={imageAltText}
      isImageOnLeft={isImageOnLeft}
      isAuthorMode={isEditing()}
      paragraph={paragraph}
      isCouponAvailable={isCouponAvailable && !!coupon}
      coupon={coupon}
      copyCouponLabel={copyCouponLabel}
      couponHelperText={couponHelperText}
      hideModalExpireTime={hideModalExpireTime ? Number(hideModalExpireTime) : undefined}
      inactivityDelay={Number(inactivityDelay)}
      timeOnPageDelay={Number(timeOnPageDelay)}
      isInactivityModalEnabled={isInactivityModalEnabled}
      isPurchasedDaysAgoModalEnabled={isPurchasedDaysAgoModalEnabled}
      purchasedDaysAgoDelay={purchasedDaysAgoDelay}
      isQuickSwipeUpModalEnabled={isQuickSwipeUpModalEnabled}
      triggerMode={triggerMode}
      priority={priority}
      isScrollDepthModalEnabled={isScrollDepthModalEnabled}
      isExitIntentEnabled={isExitIntentEnabled}
      isTimeOnPageEnabled={isTimeOnPageEnabled}
      popupId={popupId}
      frequencyType={frequencyType}
      frequencyValue={frequencyValue}
      isUserActionTriggerEnabled={isUserActionTriggerEnabled}
      userActionTrigger={userActionTrigger}
      isLocalTimeTriggerEnabled={isLocalTimeTriggerEnabled}
      localTimeWindow={localTimeWindow}
      customStartHour={customStartHour ? Number(customStartHour) : undefined}
      customEndHour={customEndHour ? Number(customEndHour) : undefined}
    />
  );
};
